import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TAP } from '../utils/layoutMode';

// ── A SHEET THAT CLOSES THE WAY A PHONE CLOSES THINGS ───────────────────────
//
// ⚠ THE COMPLAINT, VERBATIM: "never force a tap outside to cancel." Every
// slide-up in this app — the ➕ sheet, More, the ⋯ menus, every picker — could
// only be dismissed by hitting the thin strip of screen around it. On a 390px
// phone with a sheet that tall, that strip is a few dozen pixels, and missing
// it re-opens whatever is underneath.
//
// Three ways out, all of them what a phone user already expects:
//
//   1. SWIPE DOWN on the sheet itself. It follows the finger, and it only
//      closes past a real threshold — so a slow drag that changes its mind
//      springs back rather than vanishing.
//   2. TAP OUTSIDE.
//   3. THE PHONE'S BACK GESTURE / BUTTON. A history entry is pushed when the
//      sheet opens and popped when it closes, so Android's back swipe and the
//      three-button Back both dismiss the sheet instead of leaving the page —
//      which is what they did before, and is how somebody loses a half-typed
//      message.
//
// ⚠ AND A VISIBLE DRAG HANDLE, because the swipe is only discoverable if the
// sheet looks like it can be dragged.
//
// ⚠ IT NEVER SCROLLS THE PAGE BEHIND IT. body overflow is locked while open —
// without that, dragging the sheet on iOS scrolls the thread underneath and
// the sheet appears to stick.
export default function BottomSheet({
  open, onClose, title, children,
  maxHeight = '78vh',
  // A sheet that is doing something (uploading, saving) can refuse to close.
  dismissible = true,
}) {
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startY = useRef(0);
  const sheetRef = useRef(null);
  const scrollRef = useRef(null);

  const close = useCallback(() => {
    if (!dismissible) return;
    setDragY(0);
    if (onClose) onClose();
  }, [dismissible, onClose]);

  // ── THE BACK GESTURE ─────────────────────────────────────────────────────
  // ⚠ pushState ON OPEN, popped on close. Without it, Android's back swipe
  // navigates the whole app away from the thread while a sheet is up.
  useEffect(() => {
    if (!open) return undefined;
    let pushed = false;
    try { window.history.pushState({ roofbeamSheet: true }, ''); pushed = true; } catch (_) { /* older WebView */ }
    const onPop = () => { if (dismissible && onClose) onClose(); };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      // ⚠ Only unwind the entry WE pushed, and only if it is still the
      // current one — otherwise closing the sheet after the user has
      // navigated would send them back a page.
      try {
        if (pushed && window.history.state && window.history.state.roofbeamSheet) window.history.back();
      } catch (_) { /* nothing to unwind */ }
    };
  }, [open, dismissible, onClose]);

  // ── NOTHING SCROLLS BEHIND IT ────────────────────────────────────────────
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Escape, for anyone on a keyboard.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = e => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  useEffect(() => { if (!open) { setDragY(0); setDragging(false); } }, [open]);

  if (!open) return null;

  // ── THE DRAG ─────────────────────────────────────────────────────────────
  // ⚠ ONLY WHEN THE CONTENT IS SCROLLED TO THE TOP. A sheet with a long list
  // in it must let the list scroll; starting a dismiss-drag mid-list is how a
  // picker becomes impossible to read.
  const onTouchStart = e => {
    if (!dismissible) return;
    const el = scrollRef.current;
    if (el && el.scrollTop > 0) return;
    startY.current = e.touches[0].clientY;
    setDragging(true);
  };
  const onTouchMove = e => {
    if (!dragging) return;
    const dy = e.touches[0].clientY - startY.current;
    // Downward only. An upward pull does nothing rather than lifting the
    // sheet off the bottom of the screen.
    setDragY(dy > 0 ? dy : 0);
  };
  const onTouchEnd = () => {
    if (!dragging) return;
    setDragging(false);
    // ⚠ 90px, NOT "any movement". A thumb resting on a sheet moves a few
    // pixels; closing on that would make the sheet feel like it is falling
    // over. Past 90 it is a deliberate flick.
    if (dragY > 90) close(); else setDragY(0);
  };

  return (
    <>
      <div
        onClick={close}
        aria-hidden="true"
        style={{
          position: 'fixed', inset: 0, zIndex: 3000,
          background: `rgba(0,0,0,${Math.max(0.2, 0.55 - dragY / 600)})`,
          transition: dragging ? 'none' : 'background 160ms ease',
        }}
      />
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={title || 'Options'}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 3001,
          background: '#1a1d2e', borderTop: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '18px 18px 0 0', maxHeight, display: 'flex', flexDirection: 'column',
          transform: `translateY(${dragY}px)`,
          transition: dragging ? 'none' : 'transform 190ms cubic-bezier(.2,.8,.3,1)',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)',
          boxShadow: '0 -8px 40px rgba(0,0,0,0.5)',
        }}
      >
        {/* ⚠ THE HANDLE IS THE AFFORDANCE. Without it nobody discovers the
            swipe, and it is also a 44px tap target that closes the sheet. */}
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            minHeight: TAP, background: 'none', border: 'none', cursor: 'pointer',
            padding: '10px 0 4px', flexShrink: 0,
          }}
        >
          <span style={{ width: 42, height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.3)', display: 'block' }} />
        </button>
        {title && (
          <div style={{
            padding: '0 20px 8px', fontSize: 11, fontWeight: 800, letterSpacing: '0.8px',
            color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', flexShrink: 0,
          }}>{title}</div>
        )}
        <div ref={scrollRef} style={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch', minHeight: 0 }}>
          {children}
        </div>
      </div>
    </>
  );
}

// The row style every sheet's items use, so they are all the same height and
// all comfortably tappable.
export function sheetRow(active) {
  return {
    display: 'flex', alignItems: 'center', gap: 13, width: '100%',
    minHeight: TAP + 6, padding: '11px 20px', background: 'none', border: 'none',
    cursor: 'pointer', font: 'inherit', textAlign: 'left',
    color: active ? '#e91e8c' : '#fff', fontSize: 15, fontWeight: active ? 700 : 500,
  };
}
